import React from 'react';
import ProductPageTemplate from '../components/ProductPageTemplate';
import { RoleFitVisual, TeamChemistryVisual } from '../components/behavioral/FeatureVisuals';
import { SampleDiscReport } from '../components/behavioral/SampleDiscReport';

// Standalone top-level "Psychometrics" page — explains DISC and splits into two
// paths: for organizations (hiring/team fit) and for individuals (buy your own
// report at /assessment). Reuses the marketing ProductPageTemplate.
const DATA = {
  hero: {
    label: 'Psychometrics',
    title: 'Understand how people really work',
    subtitle: 'DISC is a simple, science-backed way to see how someone leads, communicates, and collaborates — the "how" behind performance that a résumé and a skills test can never show. Take it for yourself, or use it to hire and build stronger teams.',
    gradient: 'linear-gradient(150deg, #0076A3 0%, #7E8FB5 55%, #5B8C63 100%)',
    ctas: [
      { label: 'Take your own assessment — \u20b95,000 + GST', to: '/assessment', primary: true, testid: 'psy-self-cta-hero' },
      { label: 'Contact us', to: 'https://www.consultmein.com/contact-us/', testid: 'psy-org-cta-hero' },
    ],
  },
  sections: [
    {
      label: 'What Is DISC?',
      title: 'Four styles that shape how we work',
      subtitle: 'DISC is a validated behavioral model built on decades of psychological research. It measures preferences — not intelligence, not skill, and with no "good" or "bad" score. Most people are a blend of all four, with one or two that lead.',
      features: [
        { icon: '\ud83d\udd25', title: 'D \u00b7 Dominance', desc: 'How someone takes charge and drives results. High-D people are direct, decisive and comfortable with challenge, risk and pace.' },
        { icon: '\u2728', title: 'I \u00b7 Influence', desc: 'How someone connects and persuades. High-I people are outgoing, enthusiastic and energised by people, ideas and collaboration.' },
        { icon: '\ud83e\udd1d', title: 'S \u00b7 Steadiness', desc: 'How someone keeps things stable. High-S people are patient, dependable and supportive, valuing consistency and team harmony.' },
        { icon: '\ud83d\udccf', title: 'C \u00b7 Conscientiousness', desc: 'How someone handles detail and quality. High-C people are analytical, precise and careful, valuing accuracy and clear standards.' },
      ],
      columns: 2,
    },
    {
      label: 'How It Works',
      title: 'Eight minutes, honest first instinct',
      subtitle: 'A quick, engaging "most / least like me" word-choice format completed on any device — no right answers. Because the scoring is a validated, math-based model, the same answers always produce the same profile: consistent and defensible.',
      features: [
        { icon: '\u23f1\ufe0f', title: '8\u201310 Minutes', desc: 'A short forced-choice questionnaire you complete in one sitting on phone or laptop. Answers save as you go.' },
        { icon: '\ud83c\udfad', title: 'Three Graphs', desc: 'See your public self, your instinctive self under pressure, and how much you stretch to meet what you think is expected of you.' },
        { icon: '\ud83d\udde3\ufe0f', title: 'Plain-Language Report', desc: 'A friendly write-up of your strengths, working style, blind spots and the environment where you do your best work.' },
      ],
      columns: 3,
    },
    {
      label: 'For Individuals',
      title: 'Know your working style — grow your career',
      desc: 'Self-awareness is the foundation of great work. Your personal DISC report helps you play to your strengths, communicate better, and choose roles and teams where you\u2019ll genuinely thrive.',
      bullets: [
        'Understand your natural strengths and how you show up under pressure',
        'Communicate and collaborate better by knowing your own style — and adapting to others',
        'Spot blind spots and stress triggers before they cost you',
        'Choose roles, teams and managers that fit how you actually work',
        'A shareable, plain-language report you can revisit any time',
      ],
      visual: '\ud83e\udded',
      visualLabel: 'Your Personal Profile',
      visualNode: <RoleFitVisual />,
    },
    {
      label: 'Sample Report',
      title: 'See exactly what you\u2019ll get',
      desc: 'Your personal report turns your answers into a clear, plain-language picture of how you work \u2014 no jargon, no scores to decode. Here\u2019s a real sample.',
      bullets: [
        'Your natural DISC style on a simple 0\u2013100 scale',
        'A plain-language summary of your strengths and how you show up',
        'Watch-outs and stress triggers to be aware of',
        'The kind of environment where you do your best work',
      ],
      visualNode: <SampleDiscReport />,
    },
    {
      label: 'For Organizations',
      title: 'Hire for fit and build balanced teams',
      desc: 'Skills tell you if someone can do the job. Behavior tells you whether they\u2019ll thrive on your team — and stay. DISC lives right inside your CHIRE pipeline, so you get a sharper read on every candidate before you talk to them.',
      bullets: [
        'Set an ideal DISC profile per role — or build one automatically from your top performers',
        'See a Role Fit score and band (Strong / Moderate / Weak) on every profiled candidate',
        'A team chemistry map shows how a new hire complements or clashes with the current team',
        'Reduce costly mis-hires with objective, consistent, defensible scoring',
        'Interview probes and communication tips tailored to each candidate\u2019s likely blind spots',
      ],
      visual: '\ud83e\udde9',
      visualLabel: 'Team Chemistry Map',
      visualNode: <TeamChemistryVisual />,
    },
  ],
  cta: {
    title: 'Two ways to put DISC to work',
    desc: 'Discover your own working style in 8 minutes, or add psychometric insight to every role in your CHIRE pipeline.',
    ctas: [
      { label: 'For individuals — Take your assessment (\u20b95,000 + GST)', to: '/assessment', primary: true, testid: 'psy-self-cta-bottom' },
      { label: 'For organizations — Contact us', to: 'https://www.consultmein.com/contact-us/', testid: 'psy-org-cta-bottom' },
    ],
  },
};

export default function PsychometricsPage() { return <ProductPageTemplate data={DATA} />; }
